import { useState } from 'react';
import { useOne } from '@refinedev/core';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { Button, Modal, message } from 'antd';
import { conferenceCopy, DetailPage, useUrlTabState } from '@viq-admin/ui';
import type { Conference } from '@viq-admin/types';
import { archiveConference, restoreConference } from '../../api/conferences';
import { Can } from '../../ability/AbilityContext';
import { useTrackRecent } from '../../recent/recentItems';
import { ConferenceEdit } from './edit';
import { ConferenceOverview } from '../../views/ConferenceDetail/Overview';
import { ConferenceAssignments } from '../../views/ConferenceDetail/Assignments';
import { ConferenceDiscovery } from '../../views/ConferenceDetail/Discovery';
import { ConferenceResponses } from '../../views/ConferenceDetail/Responses';
import { ConferenceToolkit } from '../../views/ConferenceDetail/Toolkit';
import { ConferenceAttendeeSheet } from '../../views/ConferenceDetail/AttendeeSheet';

const TAB_KEYS = ['overview', 'assignments', 'discovery', 'responses', 'toolkit', 'attendee-sheet'];

/**
 * Conference detail — a tabbed shell (Overview / Assignments / Questions /
 * Responses / Toolkit / Attendee sheet) on the shared DetailPage, with edit folded IN-PLACE
 * (fixes the previously-unreachable `/conferences/:id/edit` route — see
 * App.tsx, which now redirects that path here). Reads the record via Refine
 * v6 `useOne`, which returns `{ query, result }` — `result` is the unwrapped
 * record. Mirrors CompanyDetail/QuestionDetail's structure; see
 * CompanyDetail's header comment for the 404-derivation rationale (a missing
 * record surfaces through useOne as `query.error` with `statusCode === 404`,
 * never an empty success).
 *
 * This ADDS two behaviors the previous bespoke `<Tabs defaultActiveKey>` shell
 * didn't have: (1) `loading > notFound > error` precedence, so a 404 id now
 * shows the Not-found Result instead of an empty "Conference" title + tabs;
 * (2) URL-synced tabs via the shared `useUrlTabState`, so reloading or
 * sharing a link lands back on the same tab instead of always resetting to
 * Overview. Both are intended parity fixes (see the branch's task report),
 * not incidental changes.
 *
 * The active/archived split still lives on ONE route (`/conferences/:id`):
 * an archived record shows Restore instead of Archive, and both the back
 * button and the "Conferences" breadcrumb crumb target `/conferences/archived`
 * instead of `/conferences` — preserving today's archived-aware navigation.
 *
 * The six tab bodies (Overview/Assignments/Questions/Responses/Toolkit/Attendee sheet) are
 * reused UNCHANGED from views/ConferenceDetail — they self-fetch and own
 * their own internal gating/loading; only this shell composition changed.
 */
export function ConferenceShow() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [editing, setEditing] = useState(false);
  const [dirty, setDirty] = useState(false);

  const { result: conference, query } = useOne<Conference>({
    resource: 'conferences',
    id,
    queryOptions: { enabled: !!id },
  });

  const [activeTab, onTabChange] = useUrlTabState(searchParams, setSearchParams, {
    keys: TAB_KEYS,
    defaultKey: 'overview',
  });

  // Record the visit for the header search's "recent" list once the record
  // has loaded (a not-found or still-loading page records nothing).
  useTrackRecent(
    conference
      ? {
          kind: 'conference',
          id: conference.id,
          label: conference.name,
          sublabel: conference.location ?? undefined,
        }
      : null,
  );

  if (!id) return null;

  const error = query?.error as { statusCode?: number } | null | undefined;
  const notFound = error?.statusCode === 404;
  const isArchived = conference?.archivedAt != null;
  const backList = conference?.archivedAt ? '/conferences/archived' : '/conferences';

  function handleArchive() {
    Modal.confirm({
      title: conferenceCopy.archiveTitle,
      content: conferenceCopy.archiveBody,
      okText: 'Archive',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await archiveConference(id!);
          message.success('Conference archived.');
          navigate('/conferences/archived');
        } catch (err: unknown) {
          message.error(
            (err as { message?: string }).message ?? 'Archive failed',
          );
        }
      },
    });
  }

  async function handleRestore() {
    try {
      await restoreConference(id!);
      message.success('Conference restored.');
      navigate('/conferences');
    } catch (err: unknown) {
      message.error((err as { message?: string }).message ?? 'Restore failed');
    }
  }

  const archiveAction = (
    <Can I="manage" a="Conference" passThrough>
      {(allowed: boolean) =>
        allowed ? (
          <Button danger={!isArchived} onClick={isArchived ? handleRestore : handleArchive}>
            {isArchived ? 'Restore' : 'Archive'}
          </Button>
        ) : null
      }
    </Can>
  );

  return (
    <DetailPage
      title={conference?.name ?? 'Conference'}
      breadcrumbs={[
        { label: 'Conferences', href: backList },
        { label: conference?.name ?? '' },
      ]}
      onNavigate={({ href }) => navigate(href)}
      onBack={() => navigate(backList)}
      loading={query?.isLoading}
      error={notFound ? undefined : query?.error}
      notFound={notFound}
      notFoundHref={backList}
      editing={editing}
      onEditingChange={(next) => {
        setEditing(next);
        // Leaving edit by ANY path (Cancel, back, a breadcrumb) must clear
        // `dirty` — not just the `onSaved` success path below. Otherwise a
        // dirty-then-discarded session leaves `dirty` stuck true, and the
        // NEXT edit session (freshly remounted, genuinely pristine) still
        // trips the discard guard on its first Cancel.
        if (!next) setDirty(false);
      }}
      editCap="manage:Conference"
      dirty={dirty}
      actions={archiveAction}
      readView={null}
      editView={
        <ConferenceEdit
          id={id}
          onDirtyChange={setDirty}
          onSaved={() => {
            setDirty(false);
            setEditing(false);
          }}
        />
      }
      tabs={[
        {
          key: 'overview',
          label: 'Overview',
          children: <ConferenceOverview conferenceId={id} />,
        },
        {
          key: 'assignments',
          label: 'Assignments',
          children: <ConferenceAssignments conferenceId={id} />,
        },
        {
          key: 'discovery',
          label: 'Questions',
          children: <ConferenceDiscovery conferenceId={id} />,
        },
        {
          key: 'responses',
          label: 'Responses',
          children: <ConferenceResponses conferenceId={id} />,
        },
        {
          key: 'toolkit',
          label: 'Toolkit',
          children: <ConferenceToolkit conferenceId={id} />,
        },
        {
          key: 'attendee-sheet',
          label: 'Attendee sheet',
          children: <ConferenceAttendeeSheet conferenceId={id} />,
        },
      ]}
      activeTab={activeTab}
      onTabChange={onTabChange}
    />
  );
}
